import React, { useState } from "react";
import Modal from "../../../shared/components/Modal";
import { FileUp, X } from "lucide-react";

const SOURCES = [
  "LinkedIn",
  "Referral",
  "Job portal",
  "Website",
  "Agency",
  "Walk-in",
  "Internal",
  "Other",
];

interface AddApplicantModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (formData: FormData) => Promise<void>;
}

const AddApplicantModal: React.FC<AddApplicantModalProps> = ({
  open,
  title,
  onClose,
  onSubmit,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [source, setSource] = useState("LinkedIn");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFirstName("");
    setLastName("");
    setEmailAddress("");
    setSource("LinkedIn");
    setFile(null);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !emailAddress.trim()) {
      setError("Please fill in first name, last name, and email address.");
      return;
    }

    const formData = new FormData();
    if (file) formData.append("resume", file);
    formData.append("first_name", firstName.trim());
    formData.append("last_name", lastName.trim());
    formData.append("email_address", emailAddress.trim());
    formData.append("source", source);

    setLoading(true);
    setError(null);
    try {
      await onSubmit(formData);
      reset();
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to add applicant.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = { color: "#0f172a" };

  return (
    <Modal open={open} title={`Add Applicant – ${title}`} onClose={loading ? () => { } : handleClose} width="480px">
      <form onSubmit={handleSubmit}>
        {error && <div className="kan-form-error">{error}</div>}

        <div className="kan-form-row">
          <div className="kan-form-group">
            <label className="kan-form-label">First Name</label>
            <input
              type="text"
              className="kan-form-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Jane"
              disabled={loading}
            />
          </div>
          <div className="kan-form-group">
            <label className="kan-form-label">Last Name</label>
            <input
              type="text"
              className="kan-form-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              disabled={loading}
            />
          </div>
        </div>

        <div className="kan-form-group">
          <label className="kan-form-label">Email Address</label>
          <input
            type="email"
            className="kan-form-input"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            placeholder="jane.doe@example.com"
            disabled={loading}
          />
        </div>

        <div className="kan-form-group">
          <label className="kan-form-label">Source</label>
          <select
            className="kan-form-input"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            disabled={loading}
            style={inputStyle}
          >
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="kan-form-group">
          <label className="kan-form-label">Resume (optional)</label>
          <div className="kan-file-input-wrap">
            <label className="kan-file-input-label">
              <FileUp size={14} />
              Choose file
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setFile(f);
                }}
                disabled={loading}
              />
            </label>
            {file && (
              <span className="kan-file-name">{file.name}</span>
            )}
          </div>
        </div>

        <button type="submit" className="kan-form-submit" disabled={loading}>
          {loading ? "Adding..." : "Add Applicant"}
        </button>
      </form>
    </Modal>
  );
};

export default AddApplicantModal;
